import { msalInstance } from "msalInstance";

const STORAGE_KEY_PREFIX = "copilot-export-jobs";

// Max time we'll trust a persisted job pointer before treating it as stale
// and discarding it without even asking the server. Independent of the
// backend's own expires_at (checked separately once we hear back from it) —
// this just bounds how long a forgotten localStorage entry can linger.
const MAX_JOB_AGE_MS = 30 * 60 * 1000;

// Reconnects an in-flight (or just-finished) Copilot export job after a
// refresh/reopen — used by every dashboard that offers a Copilot export
// (org-admin, enterprise, and eventually enterprise cost-center-scoped).
// Jobs are stored in a single localStorage entry keyed by a caller-provided
// trackerKey (e.g. "admin-usage", "enterprise-budget") so independent export
// flows — including ones running in other tabs — never collide or overwrite
// each other. Only the job's identity/params are persisted, never its
// progress — that's always re-fetched live from the server.
//
// The localStorage entry itself is namespaced per signed-in user (by MSAL
// username) so a job started by one account is never picked up by another
// account signed in later on the same browser/machine — msalLogout() already
// clears localStorage on an explicit logout, but that alone doesn't cover a
// tab closed without logging out first.
const storageKey = () => {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  return account?.username ? `${STORAGE_KEY_PREFIX}::${account.username}` : null;
};

const readAll = () => {
  try {
    const key = storageKey();
    if (!key) return {};
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeAll = (jobs) => {
  try {
    const key = storageKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(jobs));
  } catch {
    // localStorage unavailable (private mode, quota, etc.) — resume-on-refresh
    // simply won't work this session; the export itself is unaffected.
  }
};

export const saveExportJob = (trackerKey, job) => {
  const jobs = readAll();
  jobs[trackerKey] = { ...job, startedAt: Date.now() };
  writeAll(jobs);
};

export const clearExportJob = (trackerKey) => {
  const jobs = readAll();
  if (!(trackerKey in jobs)) return;
  delete jobs[trackerKey];
  writeAll(jobs);
};

// Returns the persisted job pointer for trackerKey, or null if none exists or
// it's older than MAX_JOB_AGE_MS (and clears it in the latter case).
export const loadExportJob = (trackerKey) => {
  const jobs = readAll();
  const job = jobs[trackerKey];
  if (!job?.jobId || Date.now() - job.startedAt > MAX_JOB_AGE_MS) {
    if (job) clearExportJob(trackerKey);
    return null;
  }
  return job;
};
